import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdDeleteOutline,
  MdOpenInNew,
  MdPersonSearch,
  MdStar,
  MdStarBorder,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  InlineWarning,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { indentStatusBadge } from '../../../../data/master/assetOwner';
import {
  assignIndentLine,
  getIndent,
  listIndents,
  releaseIndentLine,
  removeIndentRecommendation,
} from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';
import { loadByIdOrCode } from '../../../../utils/entityResolve';
import { formatDobDisplay } from '../../../../utils/personName';
import { formatIndianDateTime } from '../../../../utils/datetime';
import { formatTimePeriod } from '../../../../utils/timePeriod';
import { RecommendAssetsModal } from './RecommendAssetsModal';
import { RecommendPeopleModal } from './RecommendPeopleModal';
import { GroupedIndentDetailPreview } from './GroupedRequestUiPreview';
import { GROUPED_REQUEST_UI_PREVIEW } from '../../../../data/mock/groupedRequestUi';

function lineTypeLabel(lineType) {
  if (lineType === 'material') return 'Material';
  if (lineType === 'special_service') return 'Special service';
  return 'Equipment';
}

function isEquipmentLine(line) {
  return !line?.lineType || line.lineType === 'equipment';
}

function isReleasedLine(line, assignment) {
  if (assignment?.releasedAt) return true;
  const status = String(line?.status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  return status === 'released';
}

function isActiveAssignedLine(line, assignment) {
  if (isReleasedLine(line, assignment)) return false;
  if (assignment?.assignedAt && !assignment?.releasedAt) return true;
  const status = String(line?.status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  return status === 'assigned';
}

function isCompletedRecommendation(rec) {
  const status = String(rec?.status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  return status === 'completed';
}

function assignBodyFromRec(rec) {
  if (rec.userEquipmentId != null) {
    return { userEquipmentId: rec.userEquipmentId };
  }
  if (rec.equipmentId != null) {
    return { equipmentId: rec.equipmentId };
  }
  if (rec.appUserId != null) return { appUserId: rec.appUserId };
  if (rec.vendorId != null) return { vendorId: rec.vendorId };
  if (rec.villagerId != null) return { villagerId: rec.villagerId };
  return null;
}

export default function IndentDetailPage() {
  const { indentId } = useParams();

  if (GROUPED_REQUEST_UI_PREVIEW) {
    return <GroupedIndentDetailPreview requestId={indentId} />;
  }

  return <IndentDetailPageLive />;
}

function IndentDetailPageLive() {
  const { indentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const assetsModal = useDisclosure();
  const peopleModal = useDisclosure();
  const [request, setRequest] = useState(null);
  const [maxRecommendations, setMaxRecommendations] = useState(3);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeLineId, setActiveLineId] = useState(null);
  const releaseConfirm = useDisclosure();
  const releaseCancelRef = useRef(null);
  const [releaseLine, setReleaseLine] = useState(null);
  const [releaseRating, setReleaseRating] = useState(null);
  const [releaseRemark, setReleaseRemark] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loaded, maxRecs] = await Promise.all([
        loadByIdOrCode({
          param: indentId,
          listFn: listIndents,
          getFn: getIndent,
          codeKey: 'requestNo',
        }),
        getConfigValue('maxRecommendationsPerRequest'),
      ]);
      setRequest(loaded);
      setMaxRecommendations(Number(maxRecs) || 3);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load request',
        status: 'error',
        position: 'top-right',
      });
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [indentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const activeLine =
    (request?.lines || []).find((l) => l.id === activeLineId) || null;

  const openRecommend = (line) => {
    setActiveLineId(line.id);
    if (isEquipmentLine(line)) assetsModal.onOpen();
    else peopleModal.onOpen();
  };

  const openAgency = () => {
    if (request?.appUserId) {
      navigate(`/admin/users/${request.appUserId}`);
    }
  };

  const openReferredMember = (rec) => {
    const id = rec?.referredByAppUserId;
    if (!id) return;
    navigate(`/admin/users/${id}`);
  };

  const renderReferredBy = (rec) => {
    if (!rec.referredByName) {
      return <Text color="text.secondary">—</Text>;
    }
    const linked = Boolean(rec.referredByLinked && rec.referredByAppUserId);
    return (
      <VStack align="start" spacing={0.5}>
        {linked ? (
          <Text
            as="button"
            type="button"
            display="block"
            fontSize="13px"
            fontWeight="600"
            color="brand.500"
            textAlign="left"
            cursor="pointer"
            textDecoration="underline"
            textUnderlineOffset="2px"
            onClick={() => openReferredMember(rec)}
            _hover={{ color: 'brand.600' }}
          >
            {rec.referredByName}
          </Text>
        ) : (
          <Text fontSize="13px">{rec.referredByName}</Text>
        )}
        {linked ? (
          <Text
            as="button"
            type="button"
            display="block"
            fontSize="11px"
            color="green.600"
            fontWeight="600"
            textAlign="left"
            cursor="pointer"
            onClick={() => openReferredMember(rec)}
            _hover={{ textDecoration: 'underline' }}
          >
            Linked member →
          </Text>
        ) : (
          <Text fontSize="11px" color="text.secondary">
            Free text
          </Text>
        )}
      </VStack>
    );
  };

  const handleRemoveRec = async (line, rec) => {
    if (!request?.id || !line?.id || !rec?.id) return;
    setBusy(true);
    try {
      const updated = await removeIndentRecommendation(
        request.id,
        line.id,
        rec.id,
      );
      setRequest(updated);
      toast({
        title: 'Recommendation removed',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to remove',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async (line, rec) => {
    const body = assignBodyFromRec(rec);
    if (!body || !request?.id || !line?.id) {
      toast({
        title: 'Missing ids for assign',
        status: 'error',
        position: 'top-right',
      });
      return;
    }
    setBusy(true);
    try {
      const updated = await assignIndentLine(request.id, line.id, body);
      setRequest(updated);
      toast({ title: 'Work assigned', status: 'success', position: 'top-right' });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to assign',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const openRelease = (line) => {
    setReleaseLine(line);
    setReleaseRating(null);
    setReleaseRemark('');
    releaseConfirm.onOpen();
  };

  const handleRelease = async () => {
    if (!request?.id || !releaseLine?.id) return;
    setBusy(true);
    try {
      const remark = releaseRemark.trim();
      const updated = await releaseIndentLine(request.id, releaseLine.id, {
        releaseNote: remark || null,
        releaseRating: releaseRating || null,
      });
      setRequest(updated);
      releaseConfirm.onClose();
      setReleaseLine(null);
      setReleaseRating(null);
      setReleaseRemark('');
      toast({ title: 'Line released', status: 'success', position: 'top-right' });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to release',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageLoader />;

  if (!request) {
    return (
      <Box>
        <Text>Request not found</Text>
      </Box>
    );
  }

  const lines = request.lines || [];
  const isWithdrawn = request.status === 'Withdrawn';
  const isCancelled = request.status === 'Cancelled';
  const statusNote = String(request.notes || '').trim();
  const withdrawnByBuyer = /withdrawn by buyer/i.test(statusNote);
  const updatedByBuyer = /updated by buyer/i.test(statusNote);

  return (
    <Box>
      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <Heading size="lg" mb={0}>
          {request.requestNo || request.indentId}
        </Heading>
        <StatusBadge status={indentStatusBadge(request.status)}>
          {request.status}
        </StatusBadge>
      </Flex>

      {updatedByBuyer && !isWithdrawn && !isCancelled ? (
        <InlineWarning mb={4}>
          Updated by buyer
          {request.updatedAt
            ? ` · ${formatIndianDateTime(request.updatedAt)}`
            : ''}
          . Review the latest line details before recommending or assigning.
        </InlineWarning>
      ) : null}

      {isWithdrawn || isCancelled ? (
        <InlineWarning mb={4}>
          {withdrawnByBuyer || isWithdrawn
            ? `Buyer withdrew this request — no longer required${
                request.updatedAt
                  ? ` · ${formatIndianDateTime(request.updatedAt)}`
                  : ''
              }.`
            : `This request was cancelled${
                request.updatedAt
                  ? ` · ${formatIndianDateTime(request.updatedAt)}`
                  : ''
              }.`}
        </InlineWarning>
      ) : null}

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
          <VStack align="start" spacing={1}>
            <Text fontSize="12px" color="text.secondary" fontWeight="500">
              Requester
            </Text>
            <Flex align="center" gap={1} minW={0}>
              <Text fontWeight="700" noOfLines={1}>
                {request.requesterName || request.vendorName || '—'}
              </Text>
              {request.appUserId ? (
                <Tooltip label="View agency">
                  <IconButton
                    aria-label="View agency"
                    icon={<MdOpenInNew />}
                    size="sm"
                    variant="ghost"
                    color="accent.amber"
                    onClick={openAgency}
                  />
                </Tooltip>
              ) : null}
            </Flex>
            {(() => {
              const primary = String(
                request.requesterName || request.vendorName || '',
              )
                .trim()
                .toLowerCase();
              const trade = String(request.agencyTradeName || '').trim();
              const tradeIsDuplicate =
                trade && trade.toLowerCase() === primary;
              const secondary = tradeIsDuplicate
                ? request.requesterSource === 'agency'
                  ? 'Agency (mobile)'
                  : request.vendorId || 'Vendor'
                : trade ||
                  (request.requesterSource === 'agency'
                    ? 'Agency (mobile)'
                    : request.vendorId || 'Vendor');
              return (
                <Text fontSize="13px" color="text.secondary">
                  {secondary}
                </Text>
              );
            })()}
          </VStack>
          <VStack align="start" spacing={1}>
            <Text fontSize="12px" color="text.secondary" fontWeight="500">
              Requested date
            </Text>
            <Text fontWeight="700">
              {formatIndianDateTime(request.createdAt) || '—'}
            </Text>
          </VStack>
          <VStack align="start" spacing={1}>
            <Text fontSize="12px" color="text.secondary" fontWeight="500">
              Last updated
            </Text>
            <Text fontWeight="700">
              {formatIndianDateTime(request.updatedAt) || '—'}
            </Text>
          </VStack>
          <VStack align="start" spacing={1}>
            <Text fontSize="12px" color="text.secondary" fontWeight="500">
              Items
            </Text>
            <Text fontWeight="700">{lines.length || request.itemCount || 0}</Text>
          </VStack>
        </SimpleGrid>
        {statusNote ? (
          <Box mt={4} pt={3} borderTopWidth="1px" borderColor="border.subtle">
            <Text fontSize="12px" color="text.secondary" fontWeight="500" mb={1}>
              Notes
            </Text>
            <Text fontSize="14px" fontWeight="600" whiteSpace="pre-wrap">
              {statusNote}
            </Text>
          </Box>
        ) : null}
      </AppCard>

      <Heading size="sm" mb={3}>
        Request lines
      </Heading>

      <VStack align="stretch" spacing={4}>
        {lines.map((line, idx) => {
          const slotsLeft = Math.max(
            0,
            maxRecommendations - (line.recommendations?.length || 0),
          );
          const equipment = isEquipmentLine(line);
          const recs = line.recommendations || [];
          const assignment = line.assignment;
          const lineReleased = isReleasedLine(line, assignment);
          const lineAssigned = isActiveAssignedLine(line, assignment);
          const canRecommend =
            !lineAssigned &&
            !lineReleased &&
            line.status !== 'Cancelled' &&
            line.status !== 'Withdrawn' &&
            line.status !== 'Released';
          const assignedTargetId =
            assignment?.userEquipmentId ||
            assignment?.equipmentId ||
            assignment?.appUserId ||
            assignment?.vendorId ||
            assignment?.villagerId;

          const renderRecAction = (rec) => {
            const recTargetId =
              rec.userEquipmentId ||
              rec.equipmentId ||
              rec.appUserId ||
              rec.vendorId ||
              rec.villagerId;
            const matchesAssignment =
              (assignment?.recommendationId != null &&
                Number(assignment.recommendationId) === Number(rec.id)) ||
              (assignedTargetId != null &&
                Number(assignedTargetId) === Number(recTargetId));
            const completed = isCompletedRecommendation(rec);

            if (lineReleased || completed) {
              if (matchesAssignment || completed) {
                return (
                  <StatusBadge status="verified">Released</StatusBadge>
                );
              }
              return (
                <Text fontSize="13px" color="text.secondary">
                  —
                </Text>
              );
            }

            if (lineAssigned) {
              if (matchesAssignment) {
                return (
                  <StatusBadge status="verified">Assigned</StatusBadge>
                );
              }
              return (
                <Text fontSize="13px" color="text.secondary">
                  —
                </Text>
              );
            }

            return (
              <>
                <PrimaryButton
                  size="sm"
                  isDisabled={busy}
                  onClick={() => handleAssign(line, rec)}
                >
                  Assign
                </PrimaryButton>
                <Tooltip label="Remove recommendation">
                  <IconButton
                    aria-label="Remove recommendation"
                    icon={<MdDeleteOutline />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    isDisabled={busy}
                    onClick={() => handleRemoveRec(line, rec)}
                  />
                </Tooltip>
              </>
            );
          };

          return (
            <AppCard key={line.id}>
              <Flex
                justify="space-between"
                align={{ base: 'start', md: 'center' }}
                gap={3}
                wrap="wrap"
                mb={4}
              >
                <Flex align="center" gap={3} minW={0}>
                  <Box
                    w="32px"
                    h="32px"
                    borderRadius="md"
                    bg="utkarsh.amber"
                    color="utkarsh.ink"
                    fontWeight="800"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    {idx + 1}
                  </Box>
                  <Flex align="center" gap={2} wrap="wrap" minW={0}>
                    <Text fontSize="18px" fontWeight="700">
                      {line.catalogLabel || line.equipmentType || 'Line'}
                    </Text>
                    <StatusBadge status="neutral">
                      {lineTypeLabel(line.lineType)}
                    </StatusBadge>
                  </Flex>
                </Flex>
                <Flex align="center" gap={2} wrap="wrap">
                  <StatusBadge status={indentStatusBadge(line.status)}>
                    {line.status}
                  </StatusBadge>
                  {canRecommend ? (
                    <PrimaryButton
                      size="sm"
                      leftIcon={<MdPersonSearch />}
                      isDisabled={slotsLeft === 0 || busy}
                      onClick={() => openRecommend(line)}
                    >
                      {recs.length
                        ? `Add more (${slotsLeft} left)`
                        : equipment
                          ? 'Recommend'
                          : line.lineType === 'material'
                            ? 'Recommend suppliers'
                            : 'Recommend people'}
                    </PrimaryButton>
                  ) : null}
                  {lineAssigned ? (
                    <OutlineButton
                      size="sm"
                      isDisabled={busy || Boolean(assignment?.releasedAt)}
                      onClick={() => openRelease(line)}
                    >
                      Release
                    </OutlineButton>
                  ) : null}
                </Flex>
              </Flex>

              <SimpleGrid
                columns={{ base: 2, sm: 3, md: 4, lg: 7 }}
                spacing={4}
                mb={3}
                p={3}
                bg="bg.input"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                <VStack align="start" spacing={0.5} minW={0}>
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    Business unit
                  </Text>
                  <Text fontSize="15px" fontWeight="700" noOfLines={2}>
                    {line.buName || '—'}
                  </Text>
                </VStack>
                <VStack align="start" spacing={0.5} minW={0}>
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    Site
                  </Text>
                  <Text fontSize="15px" fontWeight="700" noOfLines={2}>
                    {line.siteName || '—'}
                  </Text>
                </VStack>
                <VStack align="start" spacing={0.5} minW={0}>
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    {line.lineType === 'special_service'
                      ? 'Description'
                      : 'Specification'}
                  </Text>
                  <Text fontSize="15px" fontWeight="700" noOfLines={2}>
                    {line.specification?.trim() || '—'}
                  </Text>
                </VStack>
                <VStack align="start" spacing={0.5} minW={0}>
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    Quantity
                  </Text>
                  <Text fontSize="15px" fontWeight="700">
                    {line.quantity != null && line.quantity !== ''
                      ? `${line.quantity}${line.uom ? ` ${line.uom}` : ''}`
                      : '—'}
                  </Text>
                </VStack>
                {equipment ? (
                  <VStack align="start" spacing={0.5} minW={0}>
                    <Text fontSize="11px" color="text.secondary" fontWeight="600">
                      Working hours
                    </Text>
                    <Text fontSize="15px" fontWeight="700">
                      {line.workingHours || '—'}
                    </Text>
                  </VStack>
                ) : null}
                <VStack align="start" spacing={0.5} minW={0}>
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    Duration
                  </Text>
                  <Text fontSize="15px" fontWeight="700">
                    {formatTimePeriod(line.requiredFrom, line.requiredTill) || '—'}
                  </Text>
                </VStack>
                <VStack
                  align="start"
                  spacing={0.5}
                  minW={0}
                  gridColumn={{ base: '1 / -1', md: 'auto' }}
                >
                  <Text fontSize="11px" color="text.secondary" fontWeight="600">
                    Required
                  </Text>
                  <Text fontSize="15px" fontWeight="700">
                    {formatDobDisplay(line.requiredFrom) || '—'}
                    {' → '}
                    {formatDobDisplay(line.requiredTill) || '—'}
                  </Text>
                </VStack>
              </SimpleGrid>

              {lineAssigned || assignment?.assignedAt ? (
                <Box
                  mb={3}
                  p={3}
                  bg="bg.input"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.subtle"
                >
                  <Flex align="center" gap={2} wrap="wrap" mb={1}>
                    <Text fontSize="13px" fontWeight="700">
                      Assigned
                      {assignment?.releasedAt ? ' · Released' : ''}
                    </Text>
                    {assignment?.assignedBySource ? (
                      <StatusBadge
                        status={
                          assignment.assignedByActorRaw === 'agency'
                            ? 'info'
                            : assignment.assignedByActorRaw === 'staff'
                              ? 'verified'
                              : 'neutral'
                        }
                      >
                        {assignment.assignedBySource}
                      </StatusBadge>
                    ) : null}
                  </Flex>
                  {(() => {
                    const assignedLabel =
                      assignment?.equipment?.assetId ||
                      assignment?.assetId ||
                      assignment?.personName ||
                      '—';
                    const equipmentId =
                      assignment?.userEquipmentId ||
                      assignment?.assetDbId ||
                      assignment?.equipment?.id ||
                      null;
                    const isPersonLabel =
                      Boolean(assignment?.appUserId) &&
                      assignedLabel === (assignment?.personName || '');
                    const isEquipmentLabel =
                      Boolean(equipmentId) &&
                      !isPersonLabel &&
                      assignedLabel !== '—';
                    const onOpen = isPersonLabel
                      ? () => navigate(`/admin/users/${assignment.appUserId}`)
                      : isEquipmentLabel
                        ? () =>
                            navigate(`/admin/equipments/fleet/${equipmentId}`)
                        : undefined;
                    return (
                      <Text
                        fontSize="14px"
                        fontWeight="600"
                        cursor={onOpen ? 'pointer' : undefined}
                        onClick={onOpen}
                      >
                        {assignedLabel}
                      </Text>
                    );
                  })()}
                  <Text fontSize="13px" color="text.secondary" mt={1}>
                    {[
                      assignment?.assignedByDisplay,
                      assignment?.assignedAt
                        ? formatIndianDateTime(assignment.assignedAt)
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  {assignment?.releasedAt ? (
                    <Box mt={2}>
                      <HStack spacing={2} align="center" flexWrap="wrap">
                        <Text fontSize="12px" color="text.secondary" fontWeight="600">
                          Feedback
                        </Text>
                        <HStack spacing={0.5}>
                          {[1, 2, 3, 4, 5].map((n) => {
                            const rating = Number(assignment.releaseRating);
                            const active =
                              Number.isFinite(rating) && n <= rating;
                            return (
                              <Box
                                key={n}
                                as={active ? MdStar : MdStarBorder}
                                boxSize="16px"
                                color={active ? 'brand.500' : 'gray.300'}
                              />
                            );
                          })}
                        </HStack>
                        {assignment.releaseNote ? (
                          <Text fontSize="13px" color="text.secondary">
                            {assignment.releaseNote}
                          </Text>
                        ) : null}
                      </HStack>
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {recs.length > 0 ? (
                <Box
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <Box px={3} py={2} bg="bg.input">
                    <Text fontSize="13px" fontWeight="700">
                      Recommendations ({recs.length} of {maxRecommendations})
                    </Text>
                  </Box>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        {equipment ? (
                          <>
                            <Th>Vehicle</Th>
                            <Th>Owner</Th>
                            <Th>Reg. no</Th>
                          </>
                        ) : (
                          <>
                            <Th>Person</Th>
                            <Th>Phone</Th>
                            <Th>Area</Th>
                          </>
                        )}
                        <Th>Remark</Th>
                        <Th>Referred by</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recs.map((rec) => {
                        return equipment ||
                          rec.userEquipmentId ||
                          rec.equipmentId ||
                          rec.assetCode ? (
                          <Tr key={rec.id}>
                            <Td>
                              <Text fontWeight="700">
                                {rec.assetName || rec.equipmentName || rec.assetCode}
                              </Text>
                              <Text fontSize="12px" color="text.secondary" fontFamily="mono">
                                {rec.assetCode || rec.assetId}
                              </Text>
                            </Td>
                            <Td>{rec.ownerName || '—'}</Td>
                            <Td>{rec.regNo || '—'}</Td>
                            <Td color="text.secondary">{rec.remark || '—'}</Td>
                            <Td>
                              {renderReferredBy(rec)}
                              {String(rec.status || '').toLowerCase() === 'declined' ||
                              String(rec.status || '').toLowerCase() === 'rerequested' ? (
                                <Box mt={1}>
                                  <Badge
                                    colorScheme={
                                      String(rec.status).toLowerCase() === 'rerequested'
                                        ? 'blue'
                                        : 'orange'
                                    }
                                    fontSize="10px"
                                  >
                                    {String(rec.status).toLowerCase() === 'rerequested'
                                      ? 'Re-requested'
                                      : 'Declined'}
                                  </Badge>
                                  {rec.declineReason ? (
                                    <Text fontSize="12px" color="text.secondary" mt={1}>
                                      {rec.declineReason}
                                    </Text>
                                  ) : null}
                                </Box>
                              ) : null}
                            </Td>
                            <Td>
                              <Flex gap={2} align="center">
                                {renderRecAction(rec)}
                              </Flex>
                            </Td>
                          </Tr>
                        ) : (
                          <Tr key={rec.id}>
                            <Td>
                              <Text fontWeight="700">
                                {rec.personName || rec.tradeName || '—'}
                              </Text>
                              <Text fontSize="12px" color="text.secondary">
                                {rec.roleLabel || rec.targetType || ''}
                              </Text>
                            </Td>
                            <Td>{rec.phone || '—'}</Td>
                            <Td>{rec.village || '—'}</Td>
                            <Td color="text.secondary">{rec.remark || '—'}</Td>
                            <Td>
                              {renderReferredBy(rec)}
                              {String(rec.status || '').toLowerCase() === 'declined' ||
                              String(rec.status || '').toLowerCase() === 'rerequested' ? (
                                <Box mt={1}>
                                  <Badge
                                    colorScheme={
                                      String(rec.status).toLowerCase() === 'rerequested'
                                        ? 'blue'
                                        : 'orange'
                                    }
                                    fontSize="10px"
                                  >
                                    {String(rec.status).toLowerCase() === 'rerequested'
                                      ? 'Re-requested'
                                      : 'Declined'}
                                  </Badge>
                                  {rec.declineReason ? (
                                    <Text fontSize="12px" color="text.secondary" mt={1}>
                                      {rec.declineReason}
                                    </Text>
                                  ) : null}
                                </Box>
                              ) : null}
                            </Td>
                            <Td>
                              <Flex gap={2} align="center">
                                {renderRecAction(rec)}
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text fontSize="14px" color="text.secondary">
                  {equipment
                    ? 'No recommendations yet. Click Recommend to pick vehicles.'
                    : 'No recommendations yet. Click Recommend to pick people.'}
                </Text>
              )}
            </AppCard>
          );
        })}
        {lines.length === 0 ? (
          <AppCard>
            <Text color="text.secondary">This request has no lines.</Text>
          </AppCard>
        ) : null}
      </VStack>

      <RecommendAssetsModal
        isOpen={assetsModal.isOpen}
        onClose={assetsModal.onClose}
        request={request}
        line={activeLine}
        maxRecommendations={maxRecommendations}
        onSaved={(updated) => {
          if (updated) setRequest(updated);
          else load();
        }}
      />
      <RecommendPeopleModal
        isOpen={peopleModal.isOpen}
        onClose={peopleModal.onClose}
        request={request}
        line={activeLine}
        maxRecommendations={maxRecommendations}
        onSaved={(updated) => {
          if (updated) setRequest(updated);
          else load();
        }}
      />

      <AlertDialog
        isOpen={releaseConfirm.isOpen}
        leastDestructiveRef={releaseCancelRef}
        onClose={() => {
          if (busy) return;
          releaseConfirm.onClose();
          setReleaseLine(null);
          setReleaseRating(null);
          setReleaseRemark('');
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Confirm release
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={4}>
                Mark this line as released / work completed? You can add a star
                rating and remark for performance history.
              </Text>
              <Box mb={3}>
                <Text fontSize="13px" fontWeight="600" mb={1}>
                  Rating (optional)
                </Text>
                <HStack spacing={1}>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = releaseRating != null && n <= releaseRating;
                    return (
                      <Button
                        key={n}
                        variant="ghost"
                        size="sm"
                        minW="36px"
                        px={1}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                        onClick={() =>
                          setReleaseRating((prev) => (prev === n ? null : n))
                        }
                        isDisabled={busy}
                      >
                        <Box
                          as={active ? MdStar : MdStarBorder}
                          boxSize="22px"
                          color={active ? 'brand.500' : 'gray.400'}
                        />
                      </Button>
                    );
                  })}
                </HStack>
              </Box>
              <Text fontSize="13px" fontWeight="600" mb={1}>
                Remark (optional)
              </Text>
              <Textarea
                value={releaseRemark}
                onChange={(e) => setReleaseRemark(e.target.value)}
                rows={3}
                placeholder="Any note for later review…"
                isDisabled={busy}
              />
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <OutlineButton
                ref={releaseCancelRef}
                onClick={() => {
                  releaseConfirm.onClose();
                  setReleaseLine(null);
                  setReleaseRating(null);
                  setReleaseRemark('');
                }}
                isDisabled={busy}
              >
                Cancel
              </OutlineButton>
              <PrimaryButton onClick={handleRelease} isLoading={busy}>
                Yes, release
              </PrimaryButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
